import { useCallback } from 'react';

import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager.jsx';

import { userMeDraftStore } from '../../store/users/UserMeDraftStore.ts';

import { useEventCallback } from '../../hooks/useEventCallback';
import { useI18n } from '../../hooks/useI18nFormatters';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useDevicesById } from '../../store/devices/useDevices.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';
import { useUserMe } from '../../store/users/useUserMe.ts';

import { TextRegular } from '../../components/common/text/TextRegular';
import { ContentLoader } from '../../components/content-loader/ContentLoader';
import { MemoizedDevice } from '../../components/device/Device';
import { DeviceGrid } from '../../components/device/DeviceGrid';
import { DeviceTile } from '../../components/device/DeviceTile';

export function FavoriteDevices({ selectedDeviceId, isSiblingLoading }) {
  const { i18n } = useI18n();
  const devices = useDevicesById();
  const { api } = useApi();
  const { userMe, error, isLoading } = useUserMe();

  const [tileSize] = useLocalStorageState(DeviceTile.size.medium, 'deviceTileSize');
  // Devices table mode stores null, but home favorites always render tiles.
  const favoriteTileSize = tileSize ?? DeviceTile.size.medium;

  const handleControlsClick = useCallback(
    (event, device) => {
      if (selectedDeviceId === device.id) {
        RouteManager.toHome();
        return;
      }

      RouteManager.toFavoriteDevice(device.id);
    },
    [selectedDeviceId],
  );

  const handleRequestSwap = useEventCallback(({ firstId, secondId }) => {
    const { favoriteDevices } = userMe.properties;

    const firstIndex = favoriteDevices.findIndex((deviceId) => deviceId === firstId);
    const secondIndex = favoriteDevices.findIndex((deviceId) => deviceId === secondId);

    const nextFavoriteDevices = [...favoriteDevices];

    const prevItem = nextFavoriteDevices[firstIndex];
    nextFavoriteDevices[firstIndex] = nextFavoriteDevices[secondIndex];
    nextFavoriteDevices[secondIndex] = prevItem;

    userMeDraftStore.setUserMeDraft({
      userMe: {
        ...userMe,
        properties: {
          ...userMe.properties,
          favoriteDevices: nextFavoriteDevices,
        },
      },
    });
  });

  const handleDragEnd = useEventCallback(({ didDrop }) => {
    if (didDrop === true) {
      api.users
        .updateUserMeProperties({
          id: 'favoriteDevices',
          value: userMe.properties.favoriteDevices,
        })
        .then(() => {
          userMeDraftStore.setUserMeDraftDone();
        })
        .catch((error) => {
          userMeDraftStore.setUserMeDraftFailed();
          ToastManager.handleError(error);
        });
    } else {
      userMeDraftStore.setUserMeDraftFailed();
    }
  });

  if (devices.isLoading === true || isLoading || isSiblingLoading) {
    return (
      <DeviceGrid.Root>
        {Array.from({ length: 3 }).map((_, index) => {
          return (
            <DeviceGrid.Item key={index} data-animated={true}>
              <ContentLoader.Device tileSize={favoriteTileSize} />
            </DeviceGrid.Item>
          );
        })}
      </DeviceGrid.Root>
    );
  }

  if (devices.error != null || error != null) {
    const message = devices.error?.message ?? error?.message ?? '';
    return <TextRegular>{message}</TextRegular>;
  }

  let result = null;

  if (devices.byId != null && userMe.properties.favoriteDevices != null) {
    result = userMe.properties.favoriteDevices.reduce((accumulator, favoriteDeviceId, index) => {
      const device = devices.byId[favoriteDeviceId];

      device != null &&
        accumulator.push(
          <DeviceGrid.Item
            key={favoriteDeviceId}
            style={{
              '--animation-delay': `${10 * index}ms`,
            }}
          >
            <MemoizedDevice
              deviceId={favoriteDeviceId}
              isSelected={favoriteDeviceId === selectedDeviceId}
              onControlsClick={handleControlsClick}
              onRequestSwap={handleRequestSwap}
              onDragEnd={handleDragEnd}
              tileSize={favoriteTileSize}
            />
          </DeviceGrid.Item>,
        );

      return accumulator;
    }, []);
  }

  if (result?.length > 0) {
    return <DeviceGrid.Root>{result}</DeviceGrid.Root>;
  }

  return <p>{i18n.messageFormatter(`home.noFavoriteDevices`)}</p>;
}
